import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSiteConfig } from "@/contexts/SiteConfigContext";
import { Arrow, CarIcon, Tape } from "@/components/site/parts";
import "@/styles/site.css";
import { useCopy } from "@/lib/copy";

export const NAV_LINKS = [
  { to: "/projects", key: "nav.projects", id: "projects" },
  { to: "/about", key: "nav.about", id: "about" },
  { to: "/blog", key: "nav.blog", id: "blog" },
  { to: "/survey", key: "nav.survey", id: "survey" },
];

// The admin's theme accent drives the site's blue.
const accentStyle = (config) => (/^#[0-9a-f]{6}$/i.test(config.theme?.accent || "") ? { "--blue": config.theme.accent } : undefined);

export default function PublicLayout() {
  const { config } = useSiteConfig();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = useCallback(() => setMenuOpen(false), []);

  useEffect(() => {
    window.scrollTo(0, 0);
    setMenuOpen(false);
  }, [location.pathname]);

  return (
    <div className="site" style={accentStyle(config)}>
      <Header config={config} onMenu={() => setMenuOpen(true)} />
      {menuOpen && <MobileMenu config={config} onClose={closeMenu} />}
      <main>
        <Outlet />
      </main>
      <Footer config={config} />
    </div>
  );
}

function Header({ config, onMenu }) {
  const t = useCopy();
  const first = config.name?.split(" ")[0] || "Portfolio";
  return (
    <header className="bx hdr rise" data-testid="site-header">
      <div className="hdr-bar">
        <Link to="/" className="brand" data-testid="brand-link" aria-label={`${config.name}, home`}>
          <span className="brand-badge"><CarIcon /></span>
          <span style={{ display: "flex", flexDirection: "column" }}>
            <span className="brand-name">Dilip</span>
            <span className="brand-sub">dilip-hiremath.com</span>
          </span>
        </Link>
        <nav aria-label="Main" className="main-nav">
          {NAV_LINKS.map((c) => (
            <NavLink key={c.to} to={c.to} data-testid={`nav-${c.id}`}>
              <span className="led" aria-hidden="true" />
              {t(c.key)}
            </NavLink>
          ))}
        </nav>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <Link to="/contact" className="btn btn-y" style={{ height: 48 }} data-testid="nav-contact">
            {t("nav.contact")} <Arrow />
          </Link>
          <button type="button" className="mbtn" aria-label="Open menu" aria-haspopup="dialog" onClick={onMenu} data-testid="menu-open">
            <svg aria-hidden="true" width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M3 6h12M3 12h12" /></svg>
          </button>
        </div>
      </div>
    </header>
  );
}

function MobileMenu({ config, onClose }) {
  const t = useCopy();
  const closeRef = useRef(null);
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  return (
    <div className="mmenu" role="dialog" aria-modal="true" aria-label="Menu">
      <div className="mmenu-top">
        <Link to="/" className="disp" style={{ fontSize: 18, fontWeight: 900, fontVariationSettings: "'wdth' 120", textTransform: "uppercase" }}>
          {config.name}
        </Link>
        <button ref={closeRef} type="button" className="mbtn" style={{ display: "flex" }} aria-label="Close menu" onClick={onClose}>
          <svg aria-hidden="true" width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M4 4l10 10M14 4L4 14" /></svg>
        </button>
      </div>
      <nav aria-label="Primary" className="mmenu-nav">
        {NAV_LINKS.map((c, i) => (
          <NavLink key={c.to} to={c.to} className="rise" style={{ animationDelay: `${0.05 * i}s` }}>
            <span className="disp">{t(c.key)}</span>
            <span className="mono"><i aria-hidden="true" /></span>
          </NavLink>
        ))}
      </nav>
      <div className="mmenu-foot">
        <Tape height={10} />
        <Link to="/contact" className="btn btn-y" style={{ height: 56, fontSize: 16 }}>{t("nav.contact")} <Arrow /></Link>
        {config.email && <a href={`mailto:${config.email}`} className="mono" style={{ color: "#A3A8B4", fontSize: 13 }}>{config.email}</a>}
      </div>
    </div>
  );
}

function Footer({ config }) {
  const t = useCopy();
  const year = new Date().getFullYear();
  return (
    <footer className="bx" style={{ paddingTop: 40 }} data-testid="site-footer">
      <Tape height={10} />
      <div className="footer-row">
        <span className="mono">{t("footer.line", { year, name: config.name })}</span>
        <nav aria-label="Footer" className="mono" style={{ display: "flex", gap: 18, flexWrap: "wrap" }}>
          {NAV_LINKS.map((c) => <Link key={c.to} to={c.to}>{t(c.key)}</Link>)}
          <Link to="/contact">{t("nav.contact")}</Link>
          {(config.social_links || []).map((s) => (
            <a key={s.url} href={s.url} target="_blank" rel="noopener noreferrer" data-testid={`social-${s.label.toLowerCase()}`}>
              {s.label} ↗
            </a>
          ))}
        </nav>
      </div>
    </footer>
  );
}
